import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useHistory } from "react-router-dom";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HistoryIcon from "@mui/icons-material/History";
import CalculateIcon from "@mui/icons-material/Calculate";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import {
  getCurrentProductPricing,
  getCurrentProductCost,
  updateProductCost,
  assignProductPrice,
  getProductPriceHistory,
} from "../../../services/ExportPrice";
import Alert from "@mui/material/Alert";
import { getCurrentMaterialCost } from "../../../services/ExportPrice";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const InfoCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.08)",
  height: "100%",
}));

const ProfitBox = styled(Box)(({ theme, profit }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    profit >= 0
      ? alpha(theme.palette.success.main, 0.1)
      : alpha(theme.palette.error.main, 0.1),
  border: `2px solid ${profit >= 0 ? theme.palette.success.main : theme.palette.error.main}`,
}));

function ExProductPriceView() {
  const { barcode } = useParams();
  const history = useHistory();

  // State
  const [loading, setLoading] = useState(true);
  const [productData, setProductData] = useState(null);
  const [currentPricing, setCurrentPricing] = useState(null);
  const [currentCost, setCurrentCost] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);

  // Dialog states
  const [openPriceDialog, setOpenPriceDialog] = useState(false);
  const [openCostDialog, setOpenCostDialog] = useState(false);

  const [barcodeId, setBarcodeId] = useState(null); // Store barcode ID
  const [loadingMaterialCost, setLoadingMaterialCost] = useState(false);

  // Form states for price adjustment
  const [priceForm, setPriceForm] = useState({
    priceType: "RETAIL",
    sellingPrice: "",
    note: "",
  });

  // Form states for cost adjustment
  const [costForm, setCostForm] = useState({
    materialCost: 0,
    electricityCost: 0,
    deliveryCharges: 0,
    employeeCost: 0,
    externalCost: 0,
  });

  // Real-time profit calculation
  const [profitPreview, setProfitPreview] = useState({
    profitValue: 0,
    profitPercentage: 0,
  });

  useEffect(() => {
    fetchAllData();
  }, [barcode]);

  // Update profit preview when price or cost changes
  useEffect(() => {
    if (priceForm.sellingPrice && currentCost) {
      const totalCost = currentCost.totalCost || 0;
      const profit = parseFloat(priceForm.sellingPrice) - totalCost;
      const profitPct = totalCost > 0 ? (profit / totalCost) * 100 : 0;
      setProfitPreview({
        profitValue: profit,
        profitPercentage: profitPct,
      });
    } else {
      setProfitPreview({
        profitValue: 0,
        profitPercentage: 0,
      });
    }
  }, [priceForm.sellingPrice, currentCost]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch current pricing
      const pricingData = await getCurrentProductPricing(barcode);
      console.log("Current Pricing Data:", pricingData);
      if (pricingData && pricingData !== "Something went wrong...") {
        setCurrentPricing(pricingData);

        setBarcodeId(pricingData.barcodeId);
        // Extract product info from ProductBarcode
        if (pricingData.productBarcode) {
          console.log("Product Barcode Data:", pricingData.productBarcode);
          setProductData({
            barcode: pricingData.productBarcode.barcode,
            productName: pricingData.productBarcode.productName,
          });
        }
      }

      // Fetch current cost
      const costData = await getCurrentProductCost(barcode);
      if (costData && costData !== "Something went wrong...") {
        setCurrentCost(costData);
        setCostForm({
          materialCost: costData.materialCost || 0,
          electricityCost: costData.electricityCost || 0,
          deliveryCharges: costData.deliveryCharges || 0,
          employeeCost: costData.employeeCost || 0,
          externalCost: costData.externalCost || 0,
        });
      }

      // Fetch price history
      const historyData = await getProductPriceHistory(barcode);
      if (historyData && Array.isArray(historyData)) {
        setPriceHistory(historyData);
      }
    } catch (error) {
      console.error("Error fetching product pricing data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate profit
  const calculateProfit = (sellingPrice, cost) => {
    if (!sellingPrice || !cost) return { value: 0, percentage: 0 };
    const totalCost = cost.totalCost || 0;
    const profit = sellingPrice - totalCost;
    const profitPct = totalCost > 0 ? (profit / totalCost) * 100 : 0;
    return { value: profit, percentage: profitPct };
  };

  // Handle price dialog
  const handleOpenPriceDialog = (priceType) => {
    const currentPrice =
      priceType === "RETAIL"
        ? currentPricing?.currentRetailPrice
        : currentPricing?.currentWholesalePrice;

    setPriceForm({
      priceType: priceType,
      sellingPrice: currentPrice || "",
      note: "",
    });
    setOpenPriceDialog(true);
  };

  const handleClosePriceDialog = () => {
    setOpenPriceDialog(false);
    setPriceForm({ priceType: "RETAIL", sellingPrice: "", note: "" });
    setProfitPreview({ profitValue: 0, profitPercentage: 0 });
  };

  const handleSavePrice = async () => {
    const result = await assignProductPrice(barcode, priceForm);
    if (result) {
      alert("Price updated successfully!");
      handleClosePriceDialog();
      fetchAllData();
    } else {
      alert("Failed to update price. Please try again.");
    }
  };

  // Handle cost dialog
  const handleOpenCostDialog = () => {
    setOpenCostDialog(true);
  };

  const handleCloseCostDialog = () => {
    setOpenCostDialog(false);
  };

  // Load material cost from production materials
  const handleLoadMaterialCost = async () => {
    if (!barcodeId) {
      alert("Barcode ID not available. Please refresh the page.");
      return;
    }

    setLoadingMaterialCost(true);
    try {
      const response = await getCurrentMaterialCost(barcodeId);

      if (response && response !== "Something went wrong...") {
        if (response.hasMaterials && response.totalMaterialCost > 0) {
          setCostForm((prev) => ({
            ...prev,
            materialCost: response.totalMaterialCost,
          }));
          alert(
            `Material cost loaded: Rs. ${response.totalMaterialCost.toFixed(2)}`,
          );
        } else {
          alert(
            "No active production materials found or materials are missing prices.",
          );
        }
      } else {
        alert("Failed to load material cost. Please try again.");
      }
    } catch (error) {
      console.error("Error loading material cost:", error);
      alert("Failed to load material cost. Please try again.");
    } finally {
      setLoadingMaterialCost(false);
    }
  };

  const handleSaveCost = async () => {
    const result = await updateProductCost(barcode, costForm);
    if (result) {
      alert("Cost updated successfully!");
      handleCloseCostDialog();
      fetchAllData();
    } else {
      alert("Failed to update cost. Please try again.");
    }
  };

  // Calculate total cost in real-time
  const calculateTotalCost = () => {
    return (
      parseFloat(costForm.materialCost || 0) +
      parseFloat(costForm.electricityCost || 0) +
      parseFloat(costForm.deliveryCharges || 0) +
      parseFloat(costForm.employeeCost || 0) +
      parseFloat(costForm.externalCost || 0)
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const retailProfit = calculateProfit(
    currentPricing?.currentRetailPrice,
    currentCost,
  );
  const wholesaleProfit = calculateProfit(
    currentPricing?.currentWholesalePrice,
    currentCost,
  );

  return (
    <StyledPaper elevation={0}>
      {/* Header */}
      <HeaderCard>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => history.goBack()}
              sx={{ color: "white" }}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" fontWeight="bold">
              {productData?.productName || "Product Pricing"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Barcode: {productData?.barcode || barcode}
            </Typography>
          </Grid>
        </Grid>
      </HeaderCard>

      {/* Current Pricing Section */}
      <Typography variant="h6" fontWeight="bold" mb={2}>
        <PriceCheckIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Current Pricing
      </Typography>
      <Grid container spacing={3} mb={4}>
        {/* Retail Price Card */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  Retail Price
                </Typography>
                <RoleBasedRender
                  allowedRoles={["PRODUCT_PRICE_CREATE", "ROLE_ADMIN"]}
                >
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenPriceDialog("RETAIL")}
                  >
                    Adjust
                  </Button>
                </RoleBasedRender>
              </Box>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                {currentPricing?.currentRetailPrice ? (
                  `Rs. ${currentPricing.currentRetailPrice.toFixed(2)}`
                ) : (
                  <Chip label="Not Set" color="warning" />
                )}
              </Typography>
              {currentPricing?.currentRetailPrice && currentCost && (
                <ProfitBox profit={retailProfit.value}>
                  <Typography variant="body2" color="text.secondary">
                    Profit per Unit
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={
                      retailProfit.value >= 0 ? "success.main" : "error.main"
                    }
                  >
                    Rs. {retailProfit.value.toFixed(2)} (
                    {retailProfit.percentage.toFixed(1)}%)
                  </Typography>
                </ProfitBox>
              )}
              {currentPricing?.retailPriceUpdatedAt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mt={1}
                  display="block"
                >
                  Last updated:{" "}
                  {new Date(
                    currentPricing.retailPriceUpdatedAt,
                  ).toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Wholesale Price Card */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" color="info.main" fontWeight="bold">
                  Wholesale Price
                </Typography>
                <RoleBasedRender
                  allowedRoles={["PRODUCT_PRICE_CREATE", "ROLE_ADMIN"]}
                >
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenPriceDialog("WHOLESALE")}
                  >
                    Adjust
                  </Button>
                </RoleBasedRender>
              </Box>
              <Typography variant="h4" fontWeight="bold" mb={2}>
                {currentPricing?.currentWholesalePrice ? (
                  `Rs. ${currentPricing.currentWholesalePrice.toFixed(2)}`
                ) : (
                  <Chip label="Not Set" color="warning" />
                )}
              </Typography>
              {currentPricing?.currentWholesalePrice && currentCost && (
                <ProfitBox profit={wholesaleProfit.value}>
                  <Typography variant="body2" color="text.secondary">
                    Profit per Unit
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={
                      wholesaleProfit.value >= 0 ? "success.main" : "error.main"
                    }
                  >
                    Rs. {wholesaleProfit.value.toFixed(2)} (
                    {wholesaleProfit.percentage.toFixed(1)}%)
                  </Typography>
                </ProfitBox>
              )}
              {currentPricing?.wholesalePriceUpdatedAt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mt={1}
                  display="block"
                >
                  Last updated:{" "}
                  {new Date(
                    currentPricing.wholesalePriceUpdatedAt,
                  ).toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Current Cost Section */}
      <RoleBasedRender
        allowedRoles={[
          "PRODUCT_COST_VIEW",
          "PRODUCT_COST_UPDATE",
          "PRODUCT_COST_CREATE",
          "PRODUCT_COST_DELETE",
          "ROLE_ADMIN",
        ]}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            <CalculateIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Current Cost Breakdown
          </Typography>
          <RoleBasedRender
            allowedRoles={[
              "PRODUCT_COST_CREATE",
              "ROLE_ADMIN",
            ]}
          >
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleOpenCostDialog}
            >
              Update Cost
            </Button>
          </RoleBasedRender>
        </Box>
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Material Cost</strong>
                    </TableCell>
                    <TableCell align="right">
                      Rs. {currentCost?.materialCost?.toFixed(2) || "0.00"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Electricity Cost</strong>
                    </TableCell>
                    <TableCell align="right">
                      Rs. {currentCost?.electricityCost?.toFixed(2) || "0.00"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Delivery Charges</strong>
                    </TableCell>
                    <TableCell align="right">
                      Rs. {currentCost?.deliveryCharges?.toFixed(2) || "0.00"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Employee Cost</strong>
                    </TableCell>
                    <TableCell align="right">
                      Rs. {currentCost?.employeeCost?.toFixed(2) || "0.00"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>External Cost</strong>
                    </TableCell>
                    <TableCell align="right">
                      Rs. {currentCost?.externalCost?.toFixed(2) || "0.00"}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: alpha("#1976d2", 0.1) }}>
                    <TableCell>
                      <strong>Total Cost</strong>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="primary"
                      >
                        Rs. {currentCost?.totalCost?.toFixed(2) || "0.00"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </RoleBasedRender>

      <Divider sx={{ my: 3 }} />

      {/* Price History */}
      <Typography variant="h6" fontWeight="bold" mb={2}>
        <HistoryIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Price History (Last 6 Months)
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha("#1976d2", 0.1) }}>
              <TableCell>
                <strong>Date</strong>
              </TableCell>
              <TableCell>
                <strong>Type</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Selling Price</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Cost at Time</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Profit Value</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Profit %</strong>
              </TableCell>
              <TableCell>
                <strong>Assigned By</strong>
              </TableCell>
              <TableCell>
                <strong>Note</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {priceHistory.length > 0 ? (
              priceHistory.map((record, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    {new Date(record.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={record.priceType}
                      size="small"
                      color={record.priceType === "RETAIL" ? "success" : "info"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    Rs. {record.sellingPrice.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    Rs. {record.costSnapshot?.totalCost?.toFixed(2) || "0.00"}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      color={
                        record.profitValue >= 0 ? "success.main" : "error.main"
                      }
                    >
                      Rs. {record.profitValue.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      color={
                        record.profitPercentage >= 0
                          ? "success.main"
                          : "error.main"
                      }
                    >
                      {record.profitPercentage.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell>{record.assignedBy}</TableCell>
                  <TableCell>{record.note || "-"}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No price history available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Price Adjustment Dialog - ENHANCED */}
      <Dialog
        open={openPriceDialog}
        onClose={handleClosePriceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <TrendingUpIcon sx={{ mr: 1, color: "primary.main" }} />
            Adjust {priceForm.priceType === "RETAIL"
              ? "Retail"
              : "Wholesale"}{" "}
            Price
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <TextField
              label="Price Type"
              select
              fullWidth
              value={priceForm.priceType}
              onChange={(e) =>
                setPriceForm({ ...priceForm, priceType: e.target.value })
              }
            >
              <MenuItem value="RETAIL">Retail</MenuItem>
              <MenuItem value="WHOLESALE">Wholesale</MenuItem>
            </TextField>

            <TextField
              label="Selling Price (Rs.)"
              type="number"
              fullWidth
              value={priceForm.sellingPrice}
              onChange={(e) =>
                setPriceForm({ ...priceForm, sellingPrice: e.target.value })
              }
              onWheel={(e) => e.target.blur()}
              InputProps={{
                startAdornment: (
                  <AttachMoneyIcon sx={{ mr: 1, color: "action.active" }} />
                ),
              }}
              autoFocus
              helperText="Enter the new selling price"
            />

            <TextField
              label="Note (Optional)"
              multiline
              rows={3}
              fullWidth
              value={priceForm.note}
              onChange={(e) =>
                setPriceForm({ ...priceForm, note: e.target.value })
              }
              placeholder="Reason for price change (optional)"
            />

            {/* Enhanced Profit Preview */}
            {currentCost ? (
              priceForm.sellingPrice ? (
                <ProfitBox profit={profitPreview.profitValue}>
                  <Stack spacing={1.5}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight="bold"
                      textAlign="center"
                    >
                      📊 Profit Analysis
                    </Typography>

                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">
                        Profit Value
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        color={
                          profitPreview.profitValue >= 0
                            ? "success.main"
                            : "error.main"
                        }
                      >
                        Rs. {profitPreview.profitValue.toFixed(2)}
                      </Typography>
                    </Box>

                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">
                        Profit Margin
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        color={
                          profitPreview.profitPercentage >= 0
                            ? "success.main"
                            : "error.main"
                        }
                      >
                        {profitPreview.profitPercentage.toFixed(2)}%
                      </Typography>
                    </Box>

                    <Divider />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Selling Price
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          Rs. {parseFloat(priceForm.sellingPrice).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Total Cost
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          Rs. {currentCost.totalCost.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                </ProfitBox>
              ) : (
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: alpha("#2196f3", 0.1),
                    border: `1px solid ${alpha("#2196f3", 0.3)}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="info.main"
                    textAlign="center"
                  >
                    💡 Enter a selling price to see profit calculation
                  </Typography>
                </Paper>
              )
            ) : (
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: alpha("#ff9800", 0.1),
                  border: `1px solid ${alpha("#ff9800", 0.3)}`,
                }}
              >
                <Typography
                  variant="body2"
                  color="warning.main"
                  textAlign="center"
                >
                  ⚠️ Cost data not loaded. Please update product cost first.
                </Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePriceDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSavePrice}
            disabled={!priceForm.sellingPrice}
          >
            Save Price
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cost Adjustment Dialog */}
      <Dialog
        open={openCostDialog}
        onClose={handleCloseCostDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CalculateIcon sx={{ mr: 1, color: "primary.main" }} />
            Update Product Cost
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert
              severity="info"
              action={
                <Button
                  size="small"
                  startIcon={
                    loadingMaterialCost ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutorenewIcon />
                    )
                  }
                  onClick={handleLoadMaterialCost}
                  disabled={loadingMaterialCost}
                >
                  {loadingMaterialCost ? "Loading..." : "Load from Materials"}
                </Button>
              }
            >
              Material cost can be automatically calculated from production
              materials
            </Alert>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Material Cost (Rs.)"
                  type="number"
                  fullWidth
                  value={costForm.materialCost}
                  onChange={(e) =>
                    setCostForm({ ...costForm, materialCost: e.target.value })
                  }
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Electricity Cost (Rs.)"
                  type="number"
                  fullWidth
                  value={costForm.electricityCost}
                  onChange={(e) =>
                    setCostForm({
                      ...costForm,
                      electricityCost: e.target.value,
                    })
                  }
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Delivery Charges (Rs.)"
                  type="number"
                  fullWidth
                  value={costForm.deliveryCharges}
                  onChange={(e) =>
                    setCostForm({
                      ...costForm,
                      deliveryCharges: e.target.value,
                    })
                  }
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Employee Cost (Rs.)"
                  type="number"
                  fullWidth
                  value={costForm.employeeCost}
                  onChange={(e) =>
                    setCostForm({ ...costForm, employeeCost: e.target.value })
                  }
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="External Cost (Rs.)"
                  type="number"
                  fullWidth
                  value={costForm.externalCost}
                  onChange={(e) =>
                    setCostForm({ ...costForm, externalCost: e.target.value })
                  }
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: alpha("#1976d2", 0.1),
                    border: `2px solid ${alpha("#1976d2", 0.3)}`,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Total Cost: Rs. {calculateTotalCost().toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCostDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveCost}
          >
            Save Cost
          </Button>
        </DialogActions>
      </Dialog>
    </StyledPaper>
  );
}

export default ExProductPriceView;
